import "./RadialScoreChart.scss";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import * as R from "ramda";
import React from "react";

export const getColor = (score: number): string => {
  if (score < 20) {
    return "#FF4500";
  } else if (score < 40) {
    return "#FFC107";
  } else if (score < 60) {
    return "#FFD966";
  } else if (score < 80) {
    return "#8FC924";
  } else {
    return "#7ED600";
  }
};

export const getBackgroundColor = (score: number): string => {
  if (score < 20) {
    return "#FFD9CC";
  } else if (score < 40) {
    return "#FFF3CE";
  } else if (score < 60) {
    return "#FFF2D1";
  } else if (score < 80) {
    return "#E7F5C7";
  } else {
    return "#DDEED7";
  }
};

interface RadialScoreTextProps {
  score: number;
  maxScore?: number;
}

export const RadialScoreText = ({ score, maxScore = 100 }: RadialScoreTextProps): JSX.Element => {
  const adjustedScore = Number((score / maxScore) * 100.0);
  let text = "Bad";
  if (adjustedScore < 40) {
    text = "Weak";
  } else if (adjustedScore < 60) {
    text = "Okay";
  } else if (adjustedScore < 80) {
    text = "Good";
  } else {
    text = "Excellent";
  }
  return (
    <div
      className="scoreText"
      style={{ backgroundColor: getBackgroundColor(adjustedScore), color: getColor(adjustedScore) }}
    >
      {text}
    </div>
  );
};

/**
 * @function CustomLabel
 * @description Renders only the score percentage inside the radial chart.
 * @param {object} props - Props passed by Recharts' Text component.
 */
const CustomLabel: React.FC<any> = props => {
  const { cx, cy, score } = props;
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
      <tspan x={cx} className="score">
        {score}%
      </tspan>
    </text>
  );
};

/**
 * @interface RadialChartProps
 * @description Props for the RadialScoreChart component.
 * @property {number} [size=120] - The width and height of the chart container in pixels.
 * @property {number} [strokeWidth=15] - The width of the radial bar.
 * @property {number} score - The score to display (e.g., 81 for 81%).
 * @property {string} [className] - Optional CSS class for additional styling.
 * @property {string} color - The color of the radial bar (e.g., '#82ca9d', '#ffc658').
 * @property {string} label - The label associated with the score (e.g., "Excellent", "Weak").
 */
interface RadialChartProps {
  barSizeRatio?: number;
  className?: string;
  color?: string;
  includeTextAnnotation?: boolean;
  maxScore?: number;
  score: number;
  size?: number;
}

/**
 * @function RadialScoreChart
 * @description A React component that displays a single score as a radial chart using Recharts.
 * It assumes only one score is passed and creates a single radial bar.
 * @param {RadialChartProps} props - The properties for the chart.
 * @returns {JSX.Element} The rendered radial chart component.
 */
export const RadialScoreChart: React.FC<RadialChartProps> = ({
  barSizeRatio = 5.5 / 63,
  className,
  color,
  includeTextAnnotation = false,
  maxScore = 100,
  score,
  size = 63, // Default size
}) => {
  const adjustedScore = Number((score / maxScore) * 100.0);
  const fill = R.defaultTo(getColor(score), color);
  const data = [
    {
      fill,
      maxScore,
      name: "score",
      score: adjustedScore,
    },
  ];
  const barSize = barSizeRatio * size;
  const innerRadius = size / 2 - barSize / 2;
  const outerRadius = size / 2 + barSize / 2;
  return (
    <div
      className={`${"radialScoreChartContainer"} ${R.defaultTo("", className)}`}
      //   style={{ height: size, width: size }}
    >
      <ResponsiveContainer width={size} height={size}>
        <RadialBarChart
          barSize={barSize}
          data={data}
          endAngle={-270} // End angle (full circle clockwise from startAngle)
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={90} // Start angle (top)
        >
          {/* Background circle representing 100% completion */}
          <PolarAngleAxis
            angleAxisId={0} // Unique ID for this axis
            domain={[0, maxScore]} // Domain from 0 to maxScore
            strokeWidth={0}
            tick={false} // Hide ticks
            type="number"
          />
          {/* Radial Bar for the score */}
          <RadialBar
            background={{ fill: getBackgroundColor(adjustedScore) }}
            dataKey="score"
            isAnimationActive={false}
            label={<CustomLabel score={adjustedScore.toFixed(0)} />}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      {includeTextAnnotation && <RadialScoreText score={adjustedScore} maxScore={maxScore} />}
    </div>
  );
};
